// Address validation and formatting utilities

export const REQUIRED_ADDRESS_FIELDS = ['name', 'phone', 'addressLine1', 'postalCode', 'country'];

export const ADDRESS_LABELS = {
  name: 'Full Name',
  phone: 'Phone Number',
  addressLine1: 'Street Address',
  addressLine2: 'Address Line 2',
  postalCode: 'Postal Code',
  city: 'City',
  state: 'State / Province',
  country: 'Country / Region',
};

export const emptyAddress = {
  name: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
};

/**
 * Normalize address data from various sources (Stripe, manual form, etc.)
 * @param {Object} raw - Raw address data
 * @returns {Object} Normalized address object
 */
export function normalizeAddress(raw) {
  if (!raw || typeof raw !== 'object') {
    return {...emptyAddress};
  }
  
  // Helper function to safely trim string values
  const safeTrim = (value) => {
    if (typeof value === 'string') {
      return value.trim();
    }
    return '';
  };
  
  // Extract address object if it exists
  const addressObj = raw.address || {};
  
  return {
    name: safeTrim(raw.name) || '',
    phone: safeTrim(raw.phone) || '',
    addressLine1: safeTrim(raw.addressLine1) || safeTrim(addressObj.line1) || '',
    addressLine2: safeTrim(raw.addressLine2) || safeTrim(addressObj.line2) || '',
    city: safeTrim(raw.city) || safeTrim(addressObj.city) || '',
    state: safeTrim(raw.state) || safeTrim(addressObj.state) || '',
    postalCode: safeTrim(raw.postalCode) || safeTrim(raw.zip) || safeTrim(addressObj.postal_code) || '',
    country: safeTrim(raw.country) || safeTrim(raw.countryCode) || safeTrim(addressObj.country) || '',
  };
}

/**
 * Format address lines for display
 * @param {Object} address - Address object
 * @returns {string[]} Array of formatted address lines
 */
export function formatAddressLines(address) {
  if (!address) {
    return [];
  }
  const lines = [];
  if (address.addressLine1) {
    lines.push(address.addressLine1);
  }
  if (address.addressLine2) {
    lines.push(address.addressLine2);
  }
  const cityLine = [address.city, address.state].filter(Boolean).join(', ');
  const locality = [cityLine, address.postalCode].filter(Boolean).join(' ');
  if (locality) {
    lines.push(locality);
  }
  if (address.country) {
    lines.push(address.country);
  }
  return lines;
}

/**
 * Validate address data
 * @param {Object} address - Address object to validate
 * @returns {Object} Validation result with isValid flag and missing fields array
 */
export function validateAddress(address) {
  const missing = REQUIRED_ADDRESS_FIELDS.filter((field) => {
    const value = address[field];
    return typeof value !== 'string' || value.trim().length === 0;
  });
  return {
    isValid: missing.length === 0,
    missing,
  };
}

/**
 * Get formatted validation error message
 * @param {Object} validation - Validation result from validateAddress
 * @returns {string} Formatted error message
 */
export function getValidationErrorMessage(validation) {
  if (validation.isValid) {
    return '';
  }
  const labels = validation.missing.map((field) => ADDRESS_LABELS[field] || field);
  return `Please complete the required fields: ${labels.join(', ')}`;
}

